import DashboardCard from "../../../ui/DashboardCard/DashboardCard.tsx";
import type { TeenDashboardCompletedGigItemResponse, TeenDashboardReviewItemResponse } from "../../../../types/teenDashboardTypes.ts";

type TeenReviewsBoxProps = {
  reviews: TeenDashboardReviewItemResponse[];
  completedGigs: TeenDashboardCompletedGigItemResponse[];
  loading: boolean;
  className?: string;
};

const getAssignmentContext = (gig: TeenDashboardCompletedGigItemResponse) => {
  const teensRequired = Math.max(1, gig.teensRequired || 1);
  const coveredSeats = Math.max(1, gig.coveredSeats || 1);
  if (teensRequired <= 1 && coveredSeats <= 1) {
    return null;
  }
  return `${coveredSeats} of ${teensRequired} teen seat${teensRequired === 1 ? "" : "s"}`;
};

const TeenReviewsBox = ({ reviews, completedGigs, loading, className }: TeenReviewsBoxProps) => {
  const previewCompletedGigs = completedGigs.slice(0, 2);
  const previewReviews = reviews.slice(0, 2);

  return (
    <DashboardCard
      to="/teen/reviews"
      title="Reviews & Completed Gigs"
      subtitle="Completed work, reviews, and resolved issue history from finished gigs."
      className={className}
    >
      {loading ? (
        <div className="rounded-lg border border-dashed border-myteengig-border bg-myteengig-background p-4">
          <p className="text-sm text-myteengig-text-muted">Loading reviews and completed gigs...</p>
        </div>
      ) : reviews.length === 0 && completedGigs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-myteengig-border bg-myteengig-background p-4">
          <p className="text-sm text-myteengig-text-muted">No reviews yet. Completed gigs live here too.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {previewCompletedGigs.map((gig) => {
            const assignmentContext = getAssignmentContext(gig);

            return (
              <div key={gig.gigRequestId} className="rounded-lg border border-myteengig-border bg-myteengig-background p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-myteengig-text-primary">{gig.gigTitle ?? "Completed Gig"}</p>
                    {assignmentContext ? (
                      <p className="mt-1 text-xs font-medium text-myteengig-text-muted">Assignment: {assignmentContext}</p>
                    ) : null}
                  </div>
                  <span className="w-fit rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-900">
                    Completed
                  </span>
                </div>
              </div>
            );
          })}
          {previewReviews.map((review) => (
            <div key={review.reviewId} className="rounded-lg border border-myteengig-border bg-myteengig-background p-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="min-w-0 font-semibold text-myteengig-text-primary">{review.gigTitle ?? "Completed Gig"}</p>
                <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                  {review.rating.toFixed(1)} / 5
                </span>
              </div>
              {review.message ? <p className="mt-2 text-sm text-myteengig-text-muted">{review.message}</p> : null}
            </div>
          ))}
        </div>
      )}
    </DashboardCard>
  );
};

export default TeenReviewsBox;
